"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Package,
  DollarSign,
  Calendar,
  History,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  ShoppingCart,
  CreditCard,
  User,
  Mail,
  Phone,
  Plus,
  ArrowLeft,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import api from "../services/api";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import PhoneInput, { PhoneInputRef } from "../components/PhoneInput";

interface Order {
  _id: string;
  userId: string;
  planId: {
    _id: string;
    planName: string;
    planType: string;
    duration: number;
    amount: number;
  };
  orderDate: string;
  amount: number;
  formattedAmount: string;
  currency: string;
  paymentStatus: "Pending" | "Completed" | "Failed" | "Refunded";
  status: "Created" | "Processing" | "Completed" | "Cancelled";
  receiptUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface Plan {
  _id: string;
  planName: string;
  noOfCompany: number;
  planType: string;
  duration: number;
  amount: number;
  createdDate: string;
  isActive: boolean;
}

interface OrderFormData {
  planId: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  amount: number;
  planType: string;
}

export default function OrderPage() {
  console.log("🔥 OrderPage component rendering");

  const { user: authUser } = useAuthStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<"create" | "history">("create");

  // Get planId from URL params
  const preSelectedPlanId = searchParams.get("planId");

  // Individual state variables to prevent re-rendering issues
  const [planId, setPlanId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  // Remove customerPhone state as we'll use the isolated component
  const [amount, setAmount] = useState(0);
  const [planType, setPlanType] = useState("");

  // Use ref for the isolated PhoneInput component
  const phoneInputRef = useRef<PhoneInputRef>(null);

  console.log("🔥 Current state values:", {
    customerName,
    customerEmail,
    planId,
    amount,
    planType,
    phoneValue: phoneInputRef.current?.getValue() || "not available yet",
  });

  useEffect(() => {
    if (authUser?.id) {
      fetchOrderHistory();
      fetchPlans();
    }
  }, [authUser?.id]);

  // Track if form has been initialized to prevent overriding user input
  const [formInitialized, setFormInitialized] = useState(false);

  console.log("🔥 formInitialized state:", formInitialized);

  useEffect(() => {
    console.log("🔥 Form initialization useEffect called:", {
      authUser: authUser
        ? {
            id: authUser.id,
            company_name: authUser.company_name,
            email: authUser.email,
            mobile: authUser.mobile,
          }
        : null,
      formInitialized,
    });
    if (authUser && !formInitialized) {
      console.log("🔥 Initializing form with authUser data");
      setCustomerName(authUser.company_name || "");
      setCustomerEmail(authUser.email || "");

      // Set the phone input value using the ref
      if (phoneInputRef.current) {
        phoneInputRef.current.setValue(authUser.mobile || "");
      }

      setFormInitialized(true);
    }
  }, [authUser, formInitialized]);

  // Pre-select plan if planId is provided in URL
  useEffect(() => {
    if (preSelectedPlanId && plans.length > 0) {
      const selectedPlan = plans.find((plan) => plan._id === preSelectedPlanId);
      if (selectedPlan) {
        handlePlanSelect(selectedPlan);
        // Set tab to create order
        setActiveTab("create");
        toast.success(`${selectedPlan.planName} plan selected!`);
      }
    }
  }, [preSelectedPlanId, plans]);

  const fetchOrderHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get("/orders/history");
      if (response.data.success) {
        setOrders(response.data.orders);
      }
    } catch (error: any) {
      console.error("Error fetching order history:", error);
      toast.error("Failed to load order history");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await api.get("/plans");
      if (response.data.plans) {
        setPlans(response.data.plans);
      }
    } catch (error: any) {
      console.error("Error fetching plans:", error);
      toast.error("Failed to load plans");
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanSelect = (plan: Plan) => {
    const totalAmount = calculateTotalAmount(plan.amount);
    setPlanId(plan._id);
    setAmount(totalAmount);
    setPlanType(plan.planName);
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      console.log("🔥 handleInputChange called:", {
        name,
        value,
        type: e.target.type,
      });
      console.log("🔥 Event target:", e.target);
      console.log("🔥 Document active element:", document.activeElement);

      switch (name) {
        case "customer_name":
          console.log("🔥 Updating customer_name to", value);
          setCustomerName(value);
          break;
        case "customer_email":
          console.log("🔥 Updating customer_email to", value);
          setCustomerEmail(value);
          break;
        default:
          console.log("🔥 Unknown field name:", name);
      }
    },
    [] // Empty dependency array - this prevents function recreation on state changes
  );

  const handleCreateOrder = useCallback(async () => {
    // Create orderForm object at the time of execution to avoid re-render triggers
    const orderForm = {
      planId,
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: phoneInputRef.current?.getValue() || "",
      amount,
      planType,
    };

    // Validate form
    if (!orderForm.planId) {
      toast.error("Please select a plan");
      return;
    }
    if (!orderForm.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!orderForm.customer_email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!orderForm.customer_phone.trim()) {
      toast.error("Phone number is required");
      return;
    }

    setProcessing(true);
    try {
      // First create the order
      const orderResponse = await api.post("/orders", {
        planId: orderForm.planId,
        amount: orderForm.amount,
      });

      if (orderResponse.data.success) {
        const orderId = orderResponse.data.order._id;

        // Then initiate payment
        const paymentResponse = await api.post("/create-payment", {
          order_id: orderId,
          amount: orderForm.amount,
          customer_name: orderForm.customer_name,
          customer_email: orderForm.customer_email,
          customer_phone: orderForm.customer_phone,
          planType: orderForm.planType,
        });

        if (paymentResponse.data.success) {
          // Redirect to payment gateway
          window.location.href = paymentResponse.data.paymentUrl;
        } else {
          toast.error("Payment initiation failed");
        }
      } else {
        toast.error("Order creation failed");
      }
    } catch (error: any) {
      console.error("Error creating order:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create order"
      );
    } finally {
      setProcessing(false);
    }
  }, [planId, customerName, customerEmail, amount, planType]);

  const getPaymentStatusIcon = (status: Order["paymentStatus"]) => {
    switch (status) {
      case "Completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "Failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "Pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "Refunded":
        return <DollarSign className="w-4 h-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getPaymentStatusColor = (status: Order["paymentStatus"]) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "Failed":
        return "bg-red-100 text-red-800 border-red-200";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Refunded":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatIndianDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN");
  };

  // Calculate total amount with GST and handling charges
  const calculateTotalAmount = (baseAmount: number) => {
    const gstAmount = baseAmount * 0.18; // 18% GST
    const handlingCharges = baseAmount * 0.05; // 5% Internet Handling charges
    return baseAmount + gstAmount + handlingCharges;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleDownloadReceipt = (orderId: string, receiptUrl?: string) => {
    if (receiptUrl) {
      window.open(receiptUrl, "_blank");
      toast.success("Downloading receipt...");
    } else {
      toast.error("Receipt not available for this order.");
    }
  };

  const CreateOrderForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Plan Selection */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Select a Plan</h3>
          {preSelectedPlanId && planId && (
            <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
              Plan pre-selected from Plans page
            </span>
          )}
        </div>
        {plansLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-32 bg-gray-200 animate-pulse rounded-lg"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <motion.div
                key={plan._id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  planId === plan._id
                    ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => handlePlanSelect(plan)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">
                    {plan.planName}
                  </h4>
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-500" />
                    {planId === plan._id && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {plan.planType} Plan • {plan.duration} days
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  Companies: {plan.noOfCompany}
                </p>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">
                    Base: {formatCurrency(plan.amount)}
                  </p>
                  <p className="text-sm text-gray-500">
                    + GST (18%) + Handling (5%)
                  </p>
                  <p className="text-lg font-bold text-blue-600">
                    Total: {formatCurrency(calculateTotalAmount(plan.amount))}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Customer Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Customer Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Company Name
            </label>
            <input
              type="text"
              name="customer_name"
              value={customerName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email Address
            </label>
            <input
              type="email"
              name="customer_email"
              value={customerEmail}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter email address"
            />
          </div>
          <PhoneInput
            ref={phoneInputRef}
            initialValue=""
            onPhoneChange={(value) => {
              console.log("🔥 Phone value changed:", value);
            }}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-2" />
              Amount
            </label>
            <input
              type="text"
              value={formatCurrency(amount)}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>
        </div>
      </div>

      {/* Order Summary */}
      {planId && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Order Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Plan:</span>
              <span className="font-medium">{planType}</span>
            </div>
            {(() => {
              const selectedPlan = plans.find((p) => p._id === planId);
              const baseAmount = selectedPlan?.amount || 0;
              const gstAmount = baseAmount * 0.18;
              const handlingCharges = baseAmount * 0.05;

              return (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Amount:</span>
                    <span className="font-medium">
                      {formatCurrency(baseAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST (18%):</span>
                    <span className="font-medium">
                      {formatCurrency(gstAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Internet Handling Charges (5%):
                    </span>
                    <span className="font-medium">
                      {formatCurrency(handlingCharges)}
                    </span>
                  </div>
                </>
              );
            })()}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total Amount:</span>
                <span className="text-blue-600">{formatCurrency(amount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Button */}
      <div className="flex justify-end pt-6">
        <button
          onClick={handleCreateOrder}
          disabled={!planId || processing}
          className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {processing ? (
            <>
              <Clock className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Proceed to Payment
            </>
          )}
        </button>
      </div>
    </motion.div>
  );

  const OrderHistory = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 bg-gray-200 animate-pulse rounded-lg"
            />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Orders Yet
          </h3>
          <p className="text-gray-600">Start by creating your first order!</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order, index) => (
                <motion.tr
                  key={order._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order._id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Package className="w-5 h-5 text-gray-500 mr-2" />
                      <div className="text-sm text-gray-900">
                        {order.planId?.planName || "N/A"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.formattedAmount || formatCurrency(order.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar size={16} className="mr-1" />
                      {formatIndianDate(order.orderDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPaymentStatusColor(
                        order.paymentStatus
                      )}`}
                    >
                      {getPaymentStatusIcon(order.paymentStatus)}
                      <span className="ml-1">{order.paymentStatus}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() =>
                        handleDownloadReceipt(order._id, order.receiptUrl)
                      }
                      disabled={
                        !order.receiptUrl || order.paymentStatus !== "Completed"
                      }
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download Receipt"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Receipt
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );

  if (loading && activeTab === "history") {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Orders</h1>
              <p className="text-gray-600">
                Manage your orders and view purchase history
              </p>
            </div>
            {preSelectedPlanId && (
              <Button
                onClick={() => navigate("/plans")}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Plans
              </Button>
            )}
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("create")}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "create"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Create Order
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "history"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <History className="w-4 h-4 inline mr-2" />
                Order History
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {activeTab === "create" ? <CreateOrderForm /> : <OrderHistory />}
        </div>
      </div>
    </div>
  );
}
